import React, {useEffect, useMemo, useRef} from "react";
import {routes} from "../../../../../core/models/static/routes/app-routes";
import BroadcastService from "../../../../../core/services/broadcast";
import {Redirect, Route, Switch, useHistory, useLocation} from "react-router-dom";
import CompareListView from "./compare-list";
import LookupProductInformationView from "./part-information";
import LookupSearchView from "./search";
import useAccessLevels from "../../../../hooks/use-access-levels";
import {matchPath} from "react-router";
import useQuery from "../../../../hooks/use-query";
import CompetitorPriceReportView from "./competitor-price-report";


const LookupV2Views = () => {
    const [query] = useQuery();
    const location = useLocation();
    const history = useHistory();
    const operatorAccessLevels = useAccessLevels();

    const broadcaster = useMemo(() => query?.broadcaster, [query?.broadcaster])
    const fromUserHomepageSearchQuery = useMemo(() => !!broadcaster, [broadcaster])
    const broadcasterPresent = useRef(!!broadcaster);
    broadcasterPresent.current = !!broadcaster;

    /**
     * Listens for the changes in the url and which each change:
     * - if the url is the same as [lookup.basic], changes it immediately to the basic lookup search
     */
    useEffect(() => {
        if (!!matchPath(location.pathname, {path: routes.lookup.base, exact: true})) {
            if (operatorAccessLevels.packages.lookup.nexusSearch) {
                history.replace(routes.lookup.search);
            } else if (operatorAccessLevels.packages.lookup.comparisonList) {
                history.replace(routes.lookup.compare);
            } else if (
                operatorAccessLevels.packages.lookup.viewAllCompetitorPriceReports
                || operatorAccessLevels.packages.lookup.viewMyCompetitorPriceReports
            ) {
                history.replace(routes.lookup.competitorPriceReport);
            } else {
                history.replace(routes.lookup.base.concat(routes.error.accessDeniedForSection));
            }
        }
    }, [
        history,
        location.pathname,
        operatorAccessLevels.packages.lookup.comparisonList,
        operatorAccessLevels.packages.lookup.nexusSearch,
        operatorAccessLevels.packages.lookup.viewAllCompetitorPriceReports,
        operatorAccessLevels.packages.lookup.viewMyCompetitorPriceReports,
    ])


    /**
     * With each change in the broadcaster:
     * - attaches an event lister the given broadcaster so that in case the initiator wants to close the tabs, we
     * can close them here.
     * - if the user chooses any other link that would destroy broadcaster, closes the window.
     */
    useEffect(() => {
        if (!fromUserHomepageSearchQuery) {
            if (broadcasterPresent.current) {
                window.close();
            }
            return;
        }
        const {close: bcClose} = BroadcastService.addEventListener(broadcaster, 'message', (data) => {
            if (data?.close) {
                window.close();
            }
        });
        return () => bcClose()
    }, [broadcaster, fromUserHomepageSearchQuery])

    /**
     * As soon as the component unmounts:
     * - if broadcaster is present in the query, closes the window since the user is not allowed to leave these views
     */
    useEffect(() => {
        return () => {
            if (fromUserHomepageSearchQuery) {
                window.close();
            }
        }
    }, [fromUserHomepageSearchQuery])

    return (
        <Switch>
            <Route path={routes.lookup.base} exact/>
            {
                operatorAccessLevels.packages.lookup.nexusSearch &&
                <Route path={routes.lookup.search}>
                    <LookupSearchView/>
                </Route>
            }
            {
                operatorAccessLevels.packages.lookup.comparisonList &&
                <Route path={routes.lookup.compare}>
                    <CompareListView/>
                </Route>
            }
            {
                operatorAccessLevels.packages.lookup.lookupProductInformation &&
                <Route path={routes.lookup.productInformation}>
                    <LookupProductInformationView/>
                </Route>
            }
            {
                (
                    operatorAccessLevels.packages.lookup.viewAllCompetitorPriceReports
                    || operatorAccessLevels.packages.lookup.viewMyCompetitorPriceReports
                ) &&
                <Route path={routes.lookup.competitorPriceReport}>
                    <CompetitorPriceReportView/>
                </Route>
            }
            <Redirect to={routes.lookup.base.concat(routes.error.accessDeniedForSection)}/>
        </Switch>
    );
}

export default LookupV2Views;
